import React, { useEffect } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { useImageUrl } from '@/hooks/useImageUrl';
import { debugLogger } from '@/utils/debugLogger';

interface UnifiedAvatarProps {
  src?: string;
  alt: string;
  size?: 'micro' | 'small' | 'medium';
  fallback?: string;
  showOnlineIndicator?: boolean;
  className?: string;
}

/**
 * Unified Avatar Component
 * Single component for all circular avatar displays (chat, navigation, admin)
 * Always 1:1 aspect ratio (circular crop)
 * 
 * Sizes map to variants:
 * - micro: 32px (variant 0: 80x80)
 * - small: 40px (variant 1: 200x200)
 * - medium: 48px (variant 2: 500x500)
 */
export const UnifiedAvatar: React.FC<UnifiedAvatarProps> = ({
  src,
  alt,
  size = 'small',
  fallback,
  showOnlineIndicator = false,
  className = ''
}) => {
  const sizeConfig = {
    micro: { 
      variant: 0 as const, 
      className: 'w-8 h-8',
      indicatorSize: 'w-2.5 h-2.5'
    },
    small: { 
      variant: 1 as const, 
      className: 'w-10 h-10',
      indicatorSize: 'w-3 h-3'
    },
    medium: { 
      variant: 2 as const, 
      className: 'w-12 h-12',
      indicatorSize: 'w-3.5 h-3.5'
    }
  };

  const config = sizeConfig[size];
  
  const contextMap = { 
    0: 'avatar' as const, 
    1: 'thumbnail' as const, 
    2: 'card' as const 
  };
  
  // Log quando detectar path relativo v1.5
  useEffect(() => {
    if (src && !src.startsWith('http') && src.includes('/')) {
      debugLogger.debug('UnifiedAvatar', 'Detected relative v1.5 path, will generate signed URL', {
        src: src.substring(0, 60),
        context: contextMap[config.variant]
      });
    }
  }, [src, config.variant]);
  
  // Use async hook for v1.5 signed URL support
  const { url: processedUrl, isLoading } = useImageUrl(src, contextMap[config.variant]);

  // Generate fallback text from alt (first 2 characters uppercase)
  const fallbackText = fallback || alt.substring(0, 2).toUpperCase();

  return (
    <div className={`relative rounded-full overflow-hidden ${className}`}>
      <Avatar className={config.className}>
        {/* Show loading state during async URL processing */}
        {isLoading && (
          <div className="absolute inset-0 bg-white/5 animate-pulse rounded-full" />
        )}
        {processedUrl && <AvatarImage src={processedUrl} alt={alt} />}
        <AvatarFallback className="bg-luxury-red text-white font-medium">
          {fallbackText}
        </AvatarFallback>
      </Avatar>
      
      {showOnlineIndicator && (
        <div 
          className={`absolute bottom-0 right-0 ${config.indicatorSize} bg-green-500 rounded-full border-2 border-black`}
          aria-label="Online"
        />
      )}
    </div>
  );
};
